// src/pages/AdsPower/AdsPowerManager.tsx

import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import AdminLayout from "../Admin/AdminLayout";
import Toast from "../../components/Common/Toast";

import "../../assets/styles/adspower-manager.css";

import commonService from "../../services/otherServices";
import { getErrorMessage } from "../../services/apiClient";
import { formatIST } from "../../utils/dateHelpers";

import {
    Plus,
    Search,
    Pencil,
    Trash2,
    Users,
    CircleCheck,
    Ban,
    Activity,
    X,
    Inbox
} from "lucide-react";

interface AdsPowerUser {
    id: number;
    user_id: string;
    profile_name: string;
    salesforce_email: string;
    salesforce_password: string;
    salesforce_totp_secret: string;
    status: string;
    max_concurrent_domains: number;
    current_running_jobs: number;
    notes?: string;
    created_at: string;
}

const emptyForm = {
    user_id: "",
    profile_name: "",
    salesforce_email: "",
    salesforce_password: "",
    salesforce_totp_secret: "",
    status: "active",
    max_concurrent_domains: 2,
    notes: "",
};

const AdsPowerManager: React.FC = () => {

    const [loading, setLoading] =
        useState(false);

    const [users, setUsers] =
        useState<AdsPowerUser[]>([]);

    const [editingId, setEditingId] =
        useState<number | null>(null);

    const [showForm, setShowForm] =
        useState(false);

    const [searchTerm, setSearchTerm] =
        useState("");

    const [formData, setFormData] =
        useState<any>(emptyForm);

    const [toast, setToast] =
        useState<{ type: "success" | "error"; message: string } | null>(null);

    const formRef = useRef<HTMLDivElement>(null);

    // ====================================================
    // FETCH USERS
    // ====================================================

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (showForm) {
            formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [showForm]);

    const fetchUsers = async () => {

        try {

            setLoading(true);

            const res =
                await commonService.getAllAdsPowerUsers();

            setUsers(
                res.data?.data || []
            );

        } catch (err) {

            console.error(err);

            setToast({
                type: "error",
                message: getErrorMessage(err, "Failed to load browser profiles"),
            });

        } finally {

            setLoading(false);
        }
    };

    // ====================================================
    // HANDLE INPUT
    // ====================================================

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement |
            HTMLTextAreaElement |
            HTMLSelectElement
        >
    ) => {

        setFormData({

            ...formData,

            [e.target.name]:
                e.target.value,
        });
    };

    // ====================================================
    // RESET FORM
    // ====================================================

    const resetForm = () => {

        setEditingId(null);

        setFormData(emptyForm);
    };

    const handleCancelForm = () => {
        resetForm();
        setShowForm(false);
    };

    const handleToggleAddForm = () => {

        if (showForm && !editingId) {
            setShowForm(false);
            return;
        }

        resetForm();
        setShowForm(true);
    };

    // ====================================================
    // SAVE USER
    // ====================================================

    const handleSubmit = async (
        e: React.FormEvent
    ) => {

        e.preventDefault();

        const isEditing = !!editingId;

        try {

            if (editingId) {

                await commonService.updateAdsPowerUser(
                    editingId,
                    formData
                );

            } else {

                await commonService.createAdsPowerUser(
                    formData
                );
            }

            resetForm();
            setShowForm(false);

            setToast({
                type: "success",
                message: isEditing
                    ? "Profile updated successfully"
                    : "Profile created successfully",
            });

            fetchUsers();

        } catch (err) {

            console.error(err);

            setToast({
                type: "error",
                message: getErrorMessage(
                    err,
                    isEditing ? "Failed to update profile" : "Failed to create profile"
                ),
            });
        }
    };

    // ====================================================
    // EDIT USER
    // ====================================================

    const handleEdit = (
        user: AdsPowerUser
    ) => {

        setEditingId(user.id);

        setFormData({

            user_id:
                user.user_id || "",

            profile_name:
                user.profile_name || "",

            salesforce_email:
                user.salesforce_email || "",

            salesforce_password:
                user.salesforce_password || "",

            salesforce_totp_secret:
                user.salesforce_totp_secret || "",

            status:
                user.status || "active",

            max_concurrent_domains:
                user.max_concurrent_domains || 2,

            notes:
                user.notes || "",
        });

        setShowForm(true);
    };

    // ====================================================
    // DELETE USER
    // ====================================================

    const handleDelete = async (
        id: number
    ) => {

        const confirmDelete =
            window.confirm(
                "Delete AdsPower profile?"
            );

        if (!confirmDelete) {
            return;
        }

        try {

            await commonService.deleteAdsPowerUser(
                id
            );

            setToast({
                type: "success",
                message: "Profile deleted successfully",
            });

            fetchUsers();

        } catch (err) {

            console.error(err);

            setToast({
                type: "error",
                message: getErrorMessage(err, "Failed to delete profile"),
            });
        }
    };

    // ====================================================
    // STATUS COLOR
    // ====================================================

    const getStatusClass = (
        status: string
    ) => {

        if (status === "active") {
            return "status-active";
        }

        if (status === "blocked") {
            return "status-blocked";
        }

        return "status-inactive";
    };

    // ====================================================
    // DERIVED DATA
    // ====================================================

    const filteredUsers = useMemo(() => {

        const term = searchTerm.trim().toLowerCase();

        if (!term) return users;

        return users.filter((user) =>
            (user.profile_name || "").toLowerCase().includes(term) ||
            (user.user_id || "").toLowerCase().includes(term)
        );

    }, [users, searchTerm]);

    const activeCount = useMemo(
        () => users.filter(u => u.status === "active").length,
        [users]
    );

    const blockedCount = useMemo(
        () => users.filter(u => u.status === "blocked").length,
        [users]
    );

    const runningJobs = useMemo(
        () => users.reduce((sum, u) => sum + (u.current_running_jobs || 0), 0),
        [users]
    );

    const getInitials = (name: string) => {

        const clean = (name || "").trim();

        if (!clean) return "?";

        const parts = clean.split(/\s+/);

        if (parts.length === 1) {
            return parts[0].slice(0, 2).toUpperCase();
        }

        return (parts[0][0] + parts[1][0]).toUpperCase();
    };

    // ====================================================
    // RENDER
    // ====================================================

    return (

        <AdminLayout>

            <div style={{ padding: "30px" }}>

                    {/* ===================================== */}
                    {/* HEADER */}
                    {/* ===================================== */}

                    <div className="page-header">

                        <div>

                            <h2>
                                AdsPower Browser Manager
                            </h2>

                            <p>
                                Manage browser profiles,
                                Salesforce credentials,
                                concurrency and scraping workers.
                            </p>

                        </div>

                        <button
                            type="button"
                            className="btn-primary add-profile-btn"
                            onClick={handleToggleAddForm}
                        >
                            <Plus size={16} />
                            Add Profile
                        </button>

                    </div>

                    {/* ===================================== */}
                    {/* STATS */}
                    {/* ===================================== */}

                    <div className="stat-grid">

                        <div className="stat-card">
                            <div className="stat-icon stat-icon-blue">
                                <Users size={20} />
                            </div>
                            <div>
                                <strong>{users.length}</strong>
                                <span>Total Profiles</span>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon stat-icon-green">
                                <CircleCheck size={20} />
                            </div>
                            <div>
                                <strong>{activeCount}</strong>
                                <span>Active</span>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon stat-icon-red">
                                <Ban size={20} />
                            </div>
                            <div>
                                <strong>{blockedCount}</strong>
                                <span>Blocked</span>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon stat-icon-amber">
                                <Activity size={20} />
                            </div>
                            <div>
                                <strong>{runningJobs}</strong>
                                <span>Running Jobs</span>
                            </div>
                        </div>

                    </div>

                    {/* ===================================== */}
                    {/* FORM */}
                    {/* ===================================== */}

                    {showForm && (

                        <div className="card form-card" ref={formRef}>

                            <div className="card-header form-card-header">

                                <h3>
                                    {
                                        editingId
                                            ? "Update AdsPower Profile"
                                            : "Create AdsPower Profile"
                                    }
                                </h3>

                                <button
                                    type="button"
                                    className="icon-close-btn"
                                    onClick={handleCancelForm}
                                    aria-label="Close form"
                                >
                                    <X size={18} />
                                </button>

                            </div>

                            <form
                                className="adspower-form"
                                onSubmit={handleSubmit}
                            >

                                <div className="grid-2">

                                    <div className="form-group">

                                        <label>
                                            AdsPower User ID
                                        </label>

                                        <input
                                            type="text"
                                            name="user_id"
                                            value={formData.user_id}
                                            onChange={handleChange}
                                            placeholder="e.g. k1911kix"
                                            required
                                        />

                                    </div>

                                    <div className="form-group">

                                        <label>
                                            Profile Name
                                        </label>

                                        <input
                                            type="text"
                                            name="profile_name"
                                            value={formData.profile_name}
                                            onChange={handleChange}
                                            placeholder="e.g. Carol Walker"
                                        />

                                    </div>

                                    <div className="form-group">

                                        <label>
                                            Status
                                        </label>

                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                        >

                                            <option value="active">
                                                Active
                                            </option>

                                            <option value="inactive">
                                                Inactive
                                            </option>

                                            <option value="blocked">
                                                Blocked
                                            </option>

                                        </select>

                                    </div>

                                    <div className="form-group">

                                        <label>
                                            Max Concurrent Domains
                                        </label>

                                        <input
                                            type="number"
                                            min="1"
                                            max="5"
                                            name="max_concurrent_domains"
                                            value={
                                                formData.max_concurrent_domains
                                            }
                                            onChange={handleChange}
                                        />

                                    </div>

                                </div>

                                <div className="form-group">

                                    <label>
                                        Notes
                                    </label>

                                    <textarea
                                        rows={3}
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        placeholder="Optional notes about this browser profile"
                                    />

                                </div>

                                <div className="form-actions">

                                    <button
                                        type="submit"
                                        className="btn-primary"
                                    >
                                        {
                                            editingId
                                                ? "Update Profile"
                                                : "Create Profile"
                                        }
                                    </button>

                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={handleCancelForm}
                                    >
                                        Cancel
                                    </button>

                                </div>

                            </form>

                        </div>
                    )}

                    {/* ===================================== */}
                    {/* TABLE */}
                    {/* ===================================== */}

                    <div className="card">

                        <div className="card-header table-card-header">

                            <h3>
                                Browser Profiles
                            </h3>

                            <div className="table-search">
                                <Search size={15} />
                                <input
                                    type="text"
                                    placeholder="Search by name or user ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                        </div>

                        {loading ? (

                            <div className="loading-box">
                                Loading profiles...
                            </div>

                        ) : filteredUsers.length === 0 ? (

                            <div className="empty-box">
                                <Inbox size={30} />
                                <strong>
                                    {
                                        users.length === 0
                                            ? "No browser profiles yet"
                                            : "No profiles match your search"
                                    }
                                </strong>
                                <span>
                                    {
                                        users.length === 0
                                            ? "Add your first AdsPower profile to get started."
                                            : "Try a different name or user ID."
                                    }
                                </span>
                            </div>

                        ) : (

                            <div className="table-wrapper">

                                <table className="adspower-table">

                                    <thead>

                                        <tr>

                                            <th>
                                                Profile
                                            </th>

                                            <th>
                                                Status
                                            </th>

                                            <th>
                                                Concurrency
                                            </th>

                                            <th>
                                                Running Jobs
                                            </th>

                                            <th>
                                                Created At
                                            </th>

                                            <th>
                                                Actions
                                            </th>

                                        </tr>

                                    </thead>

                                    <tbody>

                                        {filteredUsers.map((user) => (

                                            <tr key={user.id}>

                                                <td>

                                                    <div className="profile-block">

                                                        <div className="profile-avatar">
                                                            {getInitials(user.profile_name || user.user_id)}
                                                        </div>

                                                        <div>
                                                            <strong>
                                                                {
                                                                    user.profile_name ||
                                                                    "-"
                                                                }
                                                            </strong>

                                                            <span>
                                                                {user.user_id}
                                                            </span>
                                                        </div>

                                                    </div>

                                                </td>

                                                <td>

                                                    <span
                                                        className={`status-badge ${getStatusClass(user.status)}`}
                                                    >
                                                        <span className="status-dot" />
                                                        {user.status}
                                                    </span>

                                                </td>

                                                <td>
                                                    {
                                                        user.max_concurrent_domains
                                                    }
                                                </td>

                                                <td>

                                                    <span className="job-badge">
                                                        {
                                                            user.current_running_jobs
                                                        }
                                                    </span>

                                                </td>

                                                <td>

                                                    {
                                                        `${formatIST(user.created_at)} IST`
                                                    }

                                                </td>

                                                <td>

                                                    <div className="action-buttons">

                                                        <button
                                                            className="icon-btn edit-icon-btn"
                                                            onClick={() =>
                                                                handleEdit(user)
                                                            }
                                                            aria-label="Edit profile"
                                                            title="Edit"
                                                        >
                                                            <Pencil size={15} />
                                                        </button>

                                                        <button
                                                            className="icon-btn delete-icon-btn"
                                                            onClick={() =>
                                                                handleDelete(user.id)
                                                            }
                                                            aria-label="Delete profile"
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={15} />
                                                        </button>

                                                    </div>

                                                </td>

                                            </tr>
                                        ))}

                                    </tbody>

                                </table>

                            </div>
                        )}

                    </div>

                </div>

            {toast && (
                <Toast
                    type={toast.type}
                    message={toast.message}
                    onClose={() => setToast(null)}
                />
            )}
        </AdminLayout>
    );
};

export default AdsPowerManager;
